/**
 * <shipping-price-block> custom element
 *
 * Calls the parcel-intelligence shipping rates API whenever the country
 * selection, variant, or quantity changes and renders the result in-place.
 *
 * Required data attributes on the element:
 *   data-variant-id       – Shopify variant ID
 *   data-product-id       – Shopify product ID
 *   data-product-title    – variant title (for the API item name)
 *   data-price            – unit price in cents
 *   data-grams            – variant weight in grams
 *   data-requires-shipping – "true" | "false"
 *   data-currency         – ISO currency code, e.g. "EUR"
 *   data-locale           – BCP-47 locale for number formatting, e.g. "de-DE"
 */

import { fetchShippingRates } from './shipping-rates-api.js';
import { ThemeEvents } from '@theme/events';

class ShippingPriceBlock extends HTMLElement {
  #abortController = null;
  #listenersController = null;
  #calculationQueued = false;

  connectedCallback() {
    this.#attachListeners();
    this.#calculateShipping();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#listenersController?.abort();
  }

  // ─── Data helpers ──────────────────────────────────────────────────────────

  get #scope() {
    return this.closest('.shopify-section, dialog, product-card');
  }

  get #quantityInput() {
    return this.#scope?.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] quantity-selector-component input[ref="quantityInput"]`
    );
  }

  #buildItemData() {
    const d = this.dataset;
    const quantity = Number(this.#quantityInput?.value ?? d.quantity ?? 1);
    return {
      name: d.productTitle ?? '',
      quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
      grams: parseInt(d.grams ?? '0', 10),
      price: parseInt(d.price ?? '0', 10),
      requires_shipping: d.requiresShipping !== 'false',
      fulfillment_service: 'manual',
      product_id: parseInt(d.productId ?? '0', 10),
      variant_id: parseInt(d.variantId ?? '0', 10),
    };
  }

  // ─── Event listeners ───────────────────────────────────────────────────────

  #attachListeners() {
    this.#listenersController?.abort();
    this.#listenersController = new AbortController();
    const { signal } = this.#listenersController;

    // Delegation also handles a country select replaced by a section morph.
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.matches('[data-country-select]')) {
        this.#scheduleCalculation();
      }
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.target instanceof HTMLSelectElement && event.target.matches('[data-country-select]') && event.key === 'Enter') {
        event.preventDefault();
      }
    }, { signal });

    this.#scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this), { signal });
    this.#scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
      // +/- buttons, validated manual input and total-width edits all emit this
      // event. Ignore cart, accessory and other product quantity selectors.
      if (event.target !== this.#quantityInput?.closest('quantity-selector-component')) return;
      this.#scheduleCalculation();
    }, { signal });
  }

  #onVariantUpdate(event) {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    const html = event?.detail?.data?.html;
    if (!html) return;

    const newEl = html.querySelector(`shipping-price-block[data-product-id="${this.dataset.productId}"]`);
    if (!newEl) return;

    // Sync own data attributes so future reads are consistent
    Object.assign(this.dataset, newEl.dataset);

    this.#scheduleCalculation();
  }

  #scheduleCalculation() {
    this.#abortController?.abort();
    if (this.#calculationQueued) return;
    this.#calculationQueued = true;
    // Let the product form apply variant quantity constraints and the total
    // width control commit its quantity before reading the current input.
    queueMicrotask(() => {
      this.#calculationQueued = false;
      if (this.isConnected) this.#calculateShipping();
    });
  }

  // ─── API call ──────────────────────────────────────────────────────────────

  #calculateShipping() {
    const select = this.querySelector('[data-country-select]');
    const countryCode = select?.value ?? 'DE';

    const loader = this.querySelector('[data-loader]');
    const result = this.querySelector('[data-result]');

    if (loader) loader.hidden = false;
    if (result) result.hidden = true;

    // Cancel any in-flight request
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    fetchShippingRates([this.#buildItemData()], countryCode, signal)
      .then((data) => {
        if (signal.aborted) return;
        const loader = this.querySelector('[data-loader]');
        const result = this.querySelector('[data-result]');
        if (loader) loader.hidden = true;
        if (!result) return;

        const rates = Array.isArray(data?.rates) ? data.rates : [];
        if (rates.length === 0) {
          result.hidden = false;
          result.textContent = '';
          return;
        }

        const currency = this.dataset.currency ?? 'EUR';
        const locale = this.dataset.locale ?? 'de-DE';
        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        });

        // Display the first (cheapest) rate
        const price = rates[0].total_price;
        // API returns price in cents
        const formatted = formatter.format(price / 100);

        const shippingLabel = this.dataset.shippingLabel ?? 'Versand';
        result.textContent = `${shippingLabel} ${formatted}`;
        result.hidden = false;
      })
      .catch((err) => {
        if (signal.aborted || err.name === 'AbortError') return;
        const loader = this.querySelector('[data-loader]');
        const result = this.querySelector('[data-result]');
        if (loader) loader.hidden = true;
        if (result) {
          result.hidden = false;
          result.textContent = '';
        }
      });
  }
}

customElements.define('shipping-price-block', ShippingPriceBlock);
